import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueries } from '@tanstack/react-query';
import { Icon } from '@iconify/react';
import type { CampaignMetadata, MappedLeadData, MinimalLeadData } from '../../types';
import type { PipelineChartStage } from '../common/PipelineChart';
import { LeadReportModal } from '../LeadReportModal';
import { appwriteQueryFns } from '../../services/query/appwriteQueryFns';
import { getICPConfig, getProxiedImageUrl } from '../../utils/helpers';
import {
  groupRowsBySubstatus,
  selectStageRows,
  stageHasSubstatuses,
} from '../../utils/pipelineStageLeads';

interface PipelineStatusModalProps {
  open: boolean;
  /** The clicked pipeline stage (status bucket) — carries key/label/color/value. */
  stage: PipelineChartStage | null;
  /** Campaigns in the CURRENT board selection — one leads fetch each. */
  campaigns: CampaignMetadata[];
  /** Authenticated (or impersonated) user email for the cards fetch. */
  userEmail: string;
  /** Campaign ID → seller display name (registry-resolved by the board). */
  sellerLabels?: Record<string, string | null>;
  onClose: () => void;
}

/** One campaign block: collapsible header + (substatus-grouped) lead rows.
 *  Blocks render OPEN by default (Manuel); each carries its own inline
 *  loading skeleton / failure note so slow campaigns never blank the modal.
 *  Rows match the active search filter (case-insensitive across
 *  lead/company/substatus/city). */
const CampaignBlock: React.FC<{
  campaignId: string;
  campaignName: string;
  rows: MinimalLeadData[];
  stageKey: PipelineChartStage['key'];
  defaultCollapsed: boolean;
  /** This campaign's fetch is still in flight — show an inline skeleton. */
  loading?: boolean;
  /** This campaign's fetch failed — inline note instead of a blank block. */
  failed?: boolean;
  searchQuery: string;
  onLeadClick: (lead: MinimalLeadData) => void;
  /** Seller display name for this campaign (registry-resolved upstream). */
  sellerName?: string | null;
}> = ({ campaignName, rows, stageKey, defaultCollapsed, loading = false, failed = false, searchQuery, onLeadClick, sellerName }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const query = searchQuery.trim().toLowerCase();
  const matches = (row: MinimalLeadData): boolean =>
    !query ||
    row.leadName?.toLowerCase().includes(query) ||
    row.companyName?.toLowerCase().includes(query) ||
    String(row.salesSubstatus || '').toLowerCase().includes(query) ||
    row.leadCity?.toLowerCase?.().includes(query);
  const visibleRows = rows.filter(matches);
  const groups = groupRowsBySubstatus(stageKey, visibleRows);
  // A search that hits this campaign's rows auto-expands it.
  const effectivelyCollapsed = collapsed && !query;
  return (
    <section className="min-w-0 overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.015]">
      <button
        type="button"
        onClick={() => setCollapsed((open) => !open)}
        aria-expanded={!effectivelyCollapsed}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-white/[0.03] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300/40"
      >
        <Icon
          icon="bx:chevron-down"
          className={`h-3.5 w-3.5 shrink-0 text-white/40 transition-transform duration-200 ${effectivelyCollapsed ? '-rotate-90' : ''}`}
          aria-hidden="true"
        />
        <Icon icon="bx:campaign" className="h-3.5 w-3.5 shrink-0 text-cyan-200/50" aria-hidden="true" />
        {/* Campaign name — small-caps style keeps it visually distinct from
            the lead names below it. Seller sits beside it (same resolution
            the board cards use, registry fallback → team name). */}
        <h3 className="min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-[0.08em] text-cyan-100/80">
          {campaignName}
          {sellerName && (
            <span className="ml-2 inline-flex items-center gap-1 align-middle text-[9px] font-semibold normal-case tracking-normal text-white/35">
              <Icon icon="bx:user" className="h-3 w-3" aria-hidden="true" />
              {sellerName}
            </span>
          )}
        </h3>
        {loading && <Icon icon="bx:refresh" className="h-3 w-3 shrink-0 animate-spin text-white/35 motion-reduce:animate-none" aria-hidden="true" />}
        {query && <span className="rounded-md bg-cyan-300/10 px-1.5 py-0.5 text-[10px] font-bold text-cyan-200/80">{visibleRows.length}/{rows.length}</span>}
        <span className="rounded-md bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-bold text-white/50">{rows.length}</span>
      </button>
      {!effectivelyCollapsed && (
        <div className="px-3 pb-2">
          {loading ? (
            <div className="space-y-1.5 py-1" role="status" aria-busy="true">
              <div className="h-7 animate-pulse rounded-lg bg-white/[0.04]" />
              <div className="h-7 animate-pulse rounded-lg bg-white/[0.04]" />
            </div>
          ) : failed ? (
            <p className="py-2 text-center text-[11px] text-amber-200/70">
              This campaign's leads could not be loaded.
            </p>
          ) : visibleRows.length === 0 ? (
            <p className="py-2 text-center text-[11px] text-white/30">
              No leads match the search in this campaign.
            </p>
          ) : groups.length > 0 ? (
            groups.map((group) => (
              <div key={group.label}>
                <p className="mt-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/40">
                  {group.label}
                  <span className="rounded bg-white/[0.05] px-1 text-[9px] text-white/35">{group.rows.length}</span>
                </p>
                {group.rows.map((row) => <LeadRow key={String(row.id)} row={row} onLeadClick={onLeadClick} />)}
              </div>
            ))
          ) : (
            visibleRows.map((row) => <LeadRow key={String(row.id)} row={row} onLeadClick={onLeadClick} />)
          )}
        </div>
      )}
    </section>
  );
};

/** Basic lead line — 3 columns: Lead (name + role) · Company logo + name
 *  (ALL CAPS, location under it) · ICP. Click opens the full lead detail
 *  modal (same hydrating modal the leads list uses). */
const LeadRow: React.FC<{ row: MinimalLeadData; onLeadClick: (lead: MinimalLeadData) => void }> = ({ row, onLeadClick }) => {
  const icp = typeof row.icpScore === 'number' ? getICPConfig(row.icpScore) : null;
  const [logoError, setLogoError] = useState(false);
  const logoUrl = row.companyLogo
    ? getProxiedImageUrl(row.companyLogo, undefined, null)
    : undefined;
  const location = [row.leadCity, row.leadState, row.leadCountry]
    .filter((part): part is string => Boolean(part && part !== 'Unknown'))
    .join(', ');
  return (
    <button
      type="button"
      onClick={() => onLeadClick(row)}
      aria-label={`Open ${row.leadName} details`}
      className="grid w-full grid-cols-[minmax(0,1.4fr)_minmax(0,1.3fr)_52px] items-center gap-x-2 border-b border-white/[0.04] py-1.5 text-left transition-colors hover:bg-white/[0.03] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300/40"
    >
      <div className="min-w-0">
        <p className="truncate text-[12px] font-medium text-white/90">{row.leadName}</p>
        {row.leadRole && row.leadRole !== 'Unknown' && (
          <p className="truncate text-[10px] text-white/40">{row.leadRole}</p>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-2">
        {/* Company logo — tiny tile with an initials fallback, mirroring the
            leads-list company column. */}
        {logoUrl && !logoError ? (
          <img
            src={logoUrl}
            alt=""
            className="h-6 w-6 shrink-0 rounded-md object-contain ring-1 ring-inset ring-white/10"
            onError={() => setLogoError(true)}
          />
        ) : (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white/[0.06] text-[8px] font-bold text-white/45">
            {(row.companyName || '?').slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-[11px] font-semibold uppercase tracking-[0.04em] text-white/70" title={row.companyName}>{row.companyName}</p>
          {location && <p className="truncate text-[10px] text-white/40" title={location}>{location}</p>}
        </div>
      </div>
      {icp ? (
        <span
          className={`inline-flex items-center justify-center rounded-lg px-1.5 py-0.5 text-[11px] font-bold ${icp.wrapperClass} ${icp.textClass}`}
          title={icp.label}
        >
          {row.icpScore}
        </span>
      ) : (
        <span className="text-[11px] text-white/25">—</span>
      )}
    </button>
  );
};

/**
 * Team pipeline drill-down: clicking a pipeline stage in the Team overview
 * opens this modal with the BASIC lead list for that status — lead, company,
 * ICP, location — grouped by campaign, then by substatus when the stage
 * defines any. Rows fetch on open per campaign through the SHARED
 * ['leads','campaign',id] query cache (same key the Leads Intelligence tab
 * uses), so opening the modal warms data the board already reuses.
 */
export const PipelineStatusModal: React.FC<PipelineStatusModalProps> = ({
  open,
  stage,
  campaigns,
  userEmail,
  sellerLabels,
  onClose,
}) => {
  // Escape closes + body scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  // ONE QUERY PER CAMPAIGN via useQueries — the dynamic list keeps the hook
  // order stable across open/close and any selection size, while each
  // campaign renders THE MOMENT its fetch lands (previously one combined
  // query waited for the slowest campaign, so a 10-campaign selection sat
  // on skeletons for 30s+). Keys are the SHARED ['leads','campaign',id] —
  // the exact cache Leads Intelligence uses, so the modal reuses warmed
  // lists and warms them for the board.
  const campaignIds = useMemo(
    () => (open && stage ? campaigns.map((campaign) => String(campaign.id)) : []),
    [open, stage, campaigns],
  );
  const drillKey = campaignIds.join(',');
  const queries = useQueries({
    queries: campaignIds.map((campaignId) => ({
      queryKey: ['leads', 'campaign', campaignId, userEmail],
      queryFn: () => appwriteQueryFns.leads.byCampaign(campaignId, userEmail),
      enabled: open && Boolean(stage) && Boolean(userEmail),
      staleTime: 5 * 60 * 1000,
      retry: 1,
    })),
  });

  const isLoading = queries.some((query) => query.isPending && query.isEnabled);
  const hasError = queries.some((query) => query.isError && !query.data);

  const campaignLists = useMemo(
    () => campaignIds.map((campaignId, index) => ({
      campaignId,
      campaignName: campaigns[index]?.name || campaignId,
      leads: (queries[index]?.data ?? []) as MinimalLeadData[],
      loaded: Boolean(queries[index]?.data),
      failed: Boolean(queries[index]?.isError && !queries[index]?.data),
    })),
    [campaignIds, campaigns, queries],
  );

  // Campaign blocks: ONLY campaigns that HAVE leads in the clicked status.
  // Zero-lead campaigns are dropped entirely (Manuel) — no "0 leads" headers.
  // While loading, the skeleton pass below renders every campaign so the
  // structure stays visible; once done, empty campaigns vanish from the list.
  const stageRows = useMemo(
    () => (stage
      ? campaignLists
        .map((list) => ({
          ...list,
          rows: selectStageRows(stage.key, [list])[0]?.rows ?? [],
        }))
        .filter((list) => list.rows.length > 0)
      : []),
    [stage, campaignLists],
  );
  const doneCount = campaignLists.filter((list) => list.loaded || list.failed).length;

  const totalCount = stageRows.reduce((sum, group) => sum + group.rows.length, 0);
  const grouped = stage ? stageHasSubstatuses(stage.key) : false;

  // Search across lead/company/substatus/city; campaign blocks auto-expand on
  // query and hide themselves when nothing matches.
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    // Reset the search when the drill-down target changes.
    setSearchQuery('');
  }, [stage?.key, drillKey]);
  const trimmedQuery = searchQuery.trim();
  const filteredRows = useMemo(
    () => {
      if (!trimmedQuery) return stageRows;
      const q = trimmedQuery.toLowerCase();
      return stageRows
        .map((group) => ({
          ...group,
          rows: group.rows.filter((row) =>
            row.leadName?.toLowerCase().includes(q) ||
            row.companyName?.toLowerCase().includes(q) ||
            String(row.salesSubstatus || '').toLowerCase().includes(q) ||
            row.leadCity?.toLowerCase?.().includes(q)),
        }))
        .filter((group) => group.rows.length > 0);
    },
    [stageRows, trimmedQuery],
  );
  const filteredCount = filteredRows.reduce((sum, group) => sum + group.rows.length, 0);

  // Lead detail — stacked over the list (same hydrating modal the leads list
  // uses; it reads the SHARED detail cache, so this warms it for the board).
  const [selectedLead, setSelectedLead] = useState<MinimalLeadData | null>(null);

  if (!open || !stage) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-black/80 p-3 backdrop-blur-md sm:p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="relative mx-auto flex max-h-[calc(100vh-4rem)] w-full max-w-3xl flex-col overflow-hidden rounded-[24px] border border-white/8 bg-[linear-gradient(155deg,#141414,#070707)] shadow-[0_32px_120px_rgba(0,0,0,0.5)]"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${stage.label} leads`}
      >
        {/* Header — stage color + label + total. */}
        <header className="flex shrink-0 items-center gap-2.5 border-b border-white/[0.07] px-5 py-4">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: stage.color }} />
          <h2 className="min-w-0 truncate text-[15px] font-semibold text-white">{stage.label} leads</h2>
          <span className="rounded-lg bg-white/[0.07] px-2 py-0.5 text-[12px] font-bold text-white/70">{totalCount}</span>
          {/* Search — filters rows across lead/company/substatus/city. */}
          <div className="relative ml-2 hidden min-w-0 flex-1 sm:block">
            <Icon icon="bx:search" className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" aria-hidden="true" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search lead, company, substatus…"
              aria-label="Search leads in this status"
              className="h-8 w-full max-w-[260px] rounded-lg border border-white/10 bg-white/[0.04] pl-8 pr-2 text-[12px] text-white placeholder:text-white/25 focus:border-cyan-300/50 focus:outline-none focus:ring-1 focus:ring-cyan-300/30"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close leads list"
            className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.05] text-white/60 transition-colors hover:bg-white/[0.1] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40"
          >
            <Icon icon="bx:x" className="h-4.5 w-4.5" aria-hidden="true" />
          </button>
        </header>

        {/* Column header row — 3 columns: Lead · Company (location under it) · ICP. */}
        <div className="grid shrink-0 grid-cols-[minmax(0,1.4fr)_minmax(0,1.3fr)_52px] gap-x-2 border-b border-white/[0.06] bg-white/[0.02] px-8 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
          <span>Lead</span>
          <span>Company</span>
          <span className="text-center">ICP</span>
        </div>

        {/* Body — SKELETON-FIRST: while ANY fetch is in flight, every campaign
            renders as a named block with skeleton rows (the user sees the full
            structure + explicit progress immediately — no sequential trickle
            that reads as "not working"). Real lists replace all skeletons at
            once when the last fetch lands. */}
        <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-5 py-3">
          {isLoading && (
            <p className="mb-3 flex items-center gap-2 text-[11px] text-white/45" role="status" aria-live="polite">
              <Icon icon="bx:refresh" className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              Loading lead lists… {doneCount}/{campaignIds.length} campaigns
            </p>
          )}
          {isLoading && (
            <div className="space-y-3">
              {campaignIds.map((campaignId, index) => (
                <CampaignBlock
                  key={campaignId}
                  campaignId={campaignId}
                  campaignName={campaigns[index]?.name || campaignId}
                  rows={[]}
                  stageKey={stage.key}
                  defaultCollapsed={false}
                  loading
                  searchQuery=""
                  onLeadClick={setSelectedLead}
                  sellerName={sellerLabels?.[campaignId]}
                />
              ))}
            </div>
          )}
          {!isLoading && hasError && doneCount === 0 && (
            <p className="py-8 text-center text-[13px] text-white/50">
              Could not load the leads for this status. Try reopening the list.
            </p>
          )}
          {!isLoading && doneCount > 0 && filteredRows.length === 0 && !trimmedQuery && (
            <p className="py-8 text-center text-[13px] text-white/50">
              No leads found in this status across the selected campaigns.
            </p>
          )}
          {!isLoading && filteredRows.length === 0 && trimmedQuery && (
            <p className="py-8 text-center text-[13px] text-white/50">
              No leads match “{trimmedQuery}”.
            </p>
          )}
          {!isLoading && filteredRows.length > 0 && (
            <div className="space-y-3">
              {filteredRows.map((group) => (
                <CampaignBlock
                  key={group.campaignId}
                  campaignId={group.campaignId}
                  campaignName={group.campaignName}
                  rows={group.rows}
                  stageKey={stage.key}
                  failed={group.failed}
                  defaultCollapsed={false}
                  searchQuery={searchQuery}
                  onLeadClick={setSelectedLead}
                  sellerName={sellerLabels?.[group.campaignId]}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer. */}
        <footer className="flex shrink-0 items-center gap-2 border-t border-white/[0.07] px-5 py-3 text-[11px] text-white/40">
          <span>{trimmedQuery ? `${filteredCount} of ${totalCount}` : totalCount} lead{(trimmedQuery ? filteredCount : totalCount) === 1 ? '' : 's'}</span>
          <span>·</span>
          <span>{filteredRows.length} campaign{filteredRows.length === 1 ? '' : 's'}</span>
          {grouped && (
            <>
              <span>·</span>
              <span>grouped by substatus</span>
            </>
          )}
          {trimmedQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="ml-auto rounded-md border border-white/10 px-2 py-0.5 text-[10px] text-white/60 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              Clear search
            </button>
          )}
        </footer>
      </div>

      {/* Lead detail — stacked ABOVE the list modal (higher z-index); closing
          returns to the drill-down untouched. Same LeadReportModal the leads
          list uses: it hydrates the full record from the shared cache. */}
      {selectedLead && (
        <LeadReportModal
          lead={selectedLead as unknown as MappedLeadData}
          campaignId={String(selectedLead.campaignId || '')}
          onClose={() => setSelectedLead(null)}
        />
      )}
    </div>,
    document.body,
  );
};
